// Whitelist gate for a spoken coach session. It carries none of the assistant's diagnostics, lookups or
// navigation — leaving this page ends the conversation, so she names where to click instead.

import { AssistantToolErrorCode } from '../../../../../../pure-js/types/AppAssistant'
import {
  COACH_TOOL_NAME_LIST,
  CoachToolName,
  GoalSource,
  GoalStatus,
  type UserAiMemoryKind,
  type UserGoal
} from '../../../../../../pure-js/types/CoachTypes'
import { goalMatchKey } from '../../../../../../pure-js/libs/AICoachStateHelper'
import {
  SILENT_ASSISTANT_TOOL_RESPONSE,
  type AssistantToolCall,
  type AssistantToolResult
} from '../../../../shared/components/AppAssistant/realtime/AssistantRealtimeTransport'
import {
  dispatchRememberForLater,
  type AssistantToolDispatcher
} from '../../../../shared/components/AppAssistant/realtime/assistantToolDispatcher'

export type CoachSaveGoalsOutcome = {
  /** How many goals the person holds afterwards, not how many rows this call wrote. */
  goalCount: number
  /** New goals refused because their list is full. Aria has to say so instead of claiming a save. */
  droppedForLimit: number
  /** Rows this call actually wrote. 0 without droppedForLimit = everything proposed was already on the
   * list (achieved goals block their own wording too) — the one outcome that reads like a success. */
  savedCount: number
}

export type CoachToolDeps = {
  /** Persist the agreed goals for the signed-in user and report what actually happened to them. */
  saveGoals: (goals: string[]) => Promise<CoachSaveGoalsOutcome>
  /** Retire one existing goal the person has said they are done with. */
  completeGoal: (goalId: string) => Promise<CoachSaveGoalsOutcome>
  /** The goals currently on screen — Aria names one by its wording, never by an id she has never seen. */
  getGoals: () => UserGoal[]
  /** Wind the call down. The caller waits for her closing words before the session actually closes. */
  endConversation: () => void
  /** Keep what they asked her to remember, or how they want to be coached, the moment they say it. */
  rememberForLater: (note: string, kind: UserAiMemoryKind) => Promise<boolean>
}

const isAllowedCoachTool = (name: string): name is CoachToolName => COACH_TOOL_NAME_LIST.includes(name as CoachToolName)

/** Only strings survive: the model decides the wording, never the shape. */
function readGoalStrings(args: Record<string, unknown>): string[] {
  const raw = Array.isArray(args.goals) ? args.goals : []
  return raw.map((goal) => (typeof goal === 'string' ? goal.trim() : '')).filter(Boolean)
}

export function createCoachToolDispatcher(deps: CoachToolDeps): AssistantToolDispatcher {
  return async (call: AssistantToolCall): Promise<AssistantToolResult> => {
    // The whitelist first, always: one gate, and `COACH_TOOL_NAME_LIST` is the whole of it.
    if (!isAllowedCoachTool(call.name)) return { error: AssistantToolErrorCode.TOOL_NOT_ALLOWED, tool: call.name }

    switch (call.name) {
      case CoachToolName.SAVE_DEVELOPMENT_GOALS:
        return dispatchSaveGoals(deps, call.args)
      case CoachToolName.COMPLETE_DEVELOPMENT_GOAL:
        return dispatchCompleteGoal(deps, call.args)
      case CoachToolName.END_CONVERSATION:
        // The transport acknowledges this without scheduling another utterance after her goodbye.
        deps.endConversation()
        return SILENT_ASSISTANT_TOOL_RESPONSE
      case CoachToolName.REMEMBER_FOR_LATER:
        return dispatchRememberForLater(deps, call.args)
      default:
        return { error: AssistantToolErrorCode.TOOL_NOT_ALLOWED, tool: call.name }
    }
  }
}

async function dispatchSaveGoals(deps: CoachToolDeps, args: Record<string, unknown>): Promise<AssistantToolResult> {
  const goals = readGoalStrings(args)
  if (goals.length === 0)
    return { error: AssistantToolErrorCode.TOOL_FAILED, tool: CoachToolName.SAVE_DEVELOPMENT_GOALS }

  // The callable only ever writes the caller's own goals and applies its own cap.
  const outcome = await deps.saveGoals(goals)
  // A zero-write with nothing dropped for the limit is a silent duplicate — the batch matched every
  // proposed wording against the list (achieved goals included) and wrote no rows. Aria announced
  // "it's saved" here once, measured on dev: the panel never moved. Spell it out for her.
  if (outcome.savedCount === 0 && outcome.droppedForLimit === 0) {
    return {
      ...outcome,
      source: GoalSource.COACH_FLOW,
      nothingNewSaved: true,
      reason:
        'Every proposed goal already exists on the list, or matches a goal already achieved. Tell the user nothing new was saved and offer a differently-worded goal.'
    }
  }
  return { ...outcome, source: GoalSource.COACH_FLOW }
}

async function dispatchCompleteGoal(deps: CoachToolDeps, args: Record<string, unknown>): Promise<AssistantToolResult> {
  const wording = typeof args.goal === 'string' ? args.goal.trim() : ''
  if (!wording) return { error: AssistantToolErrorCode.TOOL_FAILED, tool: CoachToolName.COMPLETE_DEVELOPMENT_GOAL }

  // Matched on the same key the write path dedupes on, so "her wording" and "their goal" agree.
  const key = goalMatchKey(wording)
  const match = deps.getGoals().find((goal) => goal.status === GoalStatus.ACTIVE && goalMatchKey(goal.goal) === key)
  // Answering with the goals she can actually retire beats a bare failure she can only repeat.
  if (!match) {
    return {
      error: AssistantToolErrorCode.TOOL_FAILED,
      tool: CoachToolName.COMPLETE_DEVELOPMENT_GOAL,
      activeGoals: deps
        .getGoals()
        .filter((goal) => goal.status === GoalStatus.ACTIVE)
        .map((goal) => goal.goal)
    }
  }

  return { ...(await deps.completeGoal(match.id)), completedGoal: match.goal }
}
